import React, { useState, useRef } from 'react';
import axios from 'axios';
import { rootUrl } from '../../../helpers';
import { FaSpinner } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context';
import DataTable from "react-data-table-component";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const ActualBills = () => {
  const { allData } = useOutletContext();
  const [period, setPeriod] = useState('jan-jun');
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState(null);
  const [error, setError] = useState(null);
  const {user} = useAuth();
  const componentRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [locationFilter, setLocationFilter] = useState('');
  const [exportType, setExportType] = useState('individual'); // 'individual' or 'summary'

  // Helper function to preload image and handle errors
  const preloadImage = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setBills(null);
    let startDate, endDate;
    if (period === 'jan-jun') {
      startDate = `${year}-01-01`;
      endDate = `${year}-06-30`;
    } else {
      startDate = `${year}-07-01`;
      endDate = `${year}-12-31`;
    }
    try {
      const cid = user?.cid
      const response = await axios.get(rootUrl(`/actualBills/${cid}~${startDate}~${endDate}`));
      setBills(response.data.data || response.data);
    } catch (err) {
      setError('Failed to fetch bills.');
    } finally {
      setLoading(false);
    }
  };

  // Get a list of years for the filter (e.g., last 10 years)
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  // Get unique locations from bills
  const locations = bills ? Array.from(new Set(bills.map(b => b.location).filter(Boolean))) : [];

  // Filter bills by location if filter is set
  const filteredBills = bills && locationFilter
    ? bills.filter(b => b.location === locationFilter)
    : bills;

  const columns = [
    {
      name: 'Plot',
      selector: row => row.property_no || row.Property_no || '-',
      sortable: true,
    },
    {
      name: 'Location',
      selector: row => row.location || '-',
      sortable: true,
    },
    {
      name: 'Customer',
      selector: row => allData?.suppliers.find(a=>a.id == row.customer)?.name,
      sortable: true,
    },
    {
      name: 'Bill Amount',
      selector: row => row.amount,
      sortable: true,
    },
    {
      name: 'Description',
      selector: row => row.description,
      sortable: true,
    },
    {
      name: 'Invoice No',
      selector: row => row.invoice_no,
      sortable: true,
    },
    {
      name: 'Opening Balance',
      selector: row => row.opening_balance,
      sortable: true,
    },
  ];

  // Helper to render a single invoice as HTML for PDF
  const renderInvoiceHTML = (bill, logoAvailable = true) => {
    const logoUrl = allData?.council?.[0]?.path ? rootUrl(`/${allData.council[0].path}`) : '';
    const today = new Date();
    const billDate = bill.date ? new Date(bill.date) : null;

    return `
      <div style="width: 700px; min-height: 400px; padding: 24px; font-family: Arial, sans-serif; border: 1px solid #e5e7eb; margin-bottom: 32px; box-sizing: border-box; page-break-inside: avoid;">
        <div style="text-align: center;">
          ${logoUrl && logoAvailable ? `<img src='${logoUrl}' alt="Council Logo" style="width: 120px; height: auto; margin-bottom: 8px; display: block; margin-left: auto; margin-right: auto;" onerror="this.style.display='none';" />` : ''}
          <h2 style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1.2;">${allData?.council?.[0]?.name || 'Council'}</h2>
          <div style="font-size: 16px; margin: 4px 0 8px 0; color: #334155; font-weight: 500; line-height: 1.3;">
            Period: ${period === 'jan-jun' ? 'Jan - Jun' : 'Jul - Dec'} ${year}
          </div>
          <h3 style="font-size: 18px; font-weight: 600; margin: 8px 0 16px 0; color: #1e293b; line-height: 1.3;">PROPERTY RATES BILL</h3>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 16px; line-height: 1.4;">
          <div style="width: 48%;">
            <p style="margin: 4px 0; font-size: 14px;"><strong>Owner:</strong> ${allData?.suppliers.find(a=>a.id == bill.customer)?.name || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Plot:</strong> ${bill.property_no || bill.Property_no || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Street:</strong> ${bill.street || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Town/Zone:</strong> ${bill.location || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Description:</strong> ${bill.description || '-'}</p>
          </div>
          <div style="width: 48%;">
            <p style="margin: 4px 0; font-size: 14px;"><strong>Invoice No:</strong> ${bill.invoice_no || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Improve Value:</strong> ${bill.improvements || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Land Value:</strong> ${bill.land_value || '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Rate Used:</strong> ${bill.annual_payable ? (parseFloat(bill.annual_payable) / 2) : '-'}</p>
            <p style="margin: 4px 0; font-size: 14px;"><strong>Hectares:</strong> ${bill.land_ext || '-'}</p>
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 14px;">
          <thead>
            <tr style="background: #1e293b; color: #fff;">
              <th style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: center; font-size: 14px; font-weight: bold;">Opening Balance</th>
              <th style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: center; font-size: 14px; font-weight: bold;">Bill Amount</th>
              <th style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: center; font-size: 14px; font-weight: bold;">Actual Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: right; font-size: 14px;">${bill.opening_balance || 0}</td>
              <td style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: right; font-size: 14px;">${bill.amount || 0}</td>
              <td style="border: 1px solid #e5e7eb; padding: 10px 8px; text-align: right; font-weight: bold; font-size: 14px;">${(parseFloat(bill.opening_balance || 0) + parseFloat(bill.amount || 0))}</td>
            </tr>
          </tbody>
        </table>
      
        <div style="display: flex; justify-content: space-between; margin-top: 32px; line-height: 1.4;">
          <div>
            <p style="margin: 8px 0; font-size: 14px;"><strong>Issued By:</strong> __________________</p>
            <p style="margin: 8px 0; font-size: 14px;"><strong>Signature:</strong> __________________</p>
          </div>
          <div>
            <p style="margin: 8px 0; font-size: 14px;"><strong>Received By:</strong> _________________</p>
            <p style="margin: 8px 0; font-size: 14px;"><strong>Signature:</strong> ___________________</p>
          </div>
        </div>
      </div>
    `;
  };

  const handleExportPDF = async () => {
    if (!filteredBills || filteredBills.length === 0) return;
    setExporting(true);
    setExportType('individual');
    
    try {
      // Preload logo image to check if it's available
      const logoUrl = allData?.council?.[0]?.path ? rootUrl(`/${allData.council[0].path}`) : '';
      const logoAvailable = logoUrl ? await preloadImage(logoUrl) : false;
      
      if (logoUrl && !logoAvailable) {
        console.warn('Logo image failed to load:', logoUrl, '- PDF will be generated without logo');
      }
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfWidth = pageWidth - 20;
      const margin = 10; // Top and bottom margins
      const availableHeight = pageHeight - (2 * margin); // Available space per page
      
      // Process bills in batches to avoid memory issues
      const batchSize = 10; // Process 10 bills at a time
      const totalBills = filteredBills.length;
      let processedBills = 0;
      let currentY = margin; // Track current Y position on page
      
      for (let batchStart = 0; batchStart < totalBills; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, totalBills);
        const batch = filteredBills.slice(batchStart, batchEnd);
        
        // Process each bill in the current batch
        for (let i = 0; i < batch.length; i++) {
          const bill = batch[i];
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.top = '-9999px';
          tempDiv.style.width = '700px';
          tempDiv.innerHTML = renderInvoiceHTML(bill, logoAvailable);
          document.body.appendChild(tempDiv);
          
          try {
            const canvas = await html2canvas(tempDiv, { 
              scale: 2, // Higher scale for better text quality
              useCORS: true,
              allowTaint: false,
              backgroundColor: '#ffffff',
              width: 700, // Fixed width to match our HTML
              height: tempDiv.scrollHeight, // Use actual content height
              ignoreElements: (element) => {
                // Ignore elements that failed to load
                return element.tagName === 'IMG' && !element.complete;
              },
              onclone: (clonedDoc) => {
                // Handle image loading in cloned document
                const images = clonedDoc.querySelectorAll('img');
                images.forEach(img => {
                  if (!img.complete || img.naturalWidth === 0) {
                    img.style.display = 'none';
                  }
                });
              }
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.9); // Higher quality JPEG
            
            // Calculate actual height based on content to avoid stretching
            let actualHeight = (canvas.height * pdfWidth) / canvas.width;
            
            // Safety check: if bill is too tall for a single page, scale it down
            const maxBillHeight = availableHeight - 20; // Leave some margin
            if (actualHeight > maxBillHeight) {
              const scaleFactor = maxBillHeight / actualHeight;
              actualHeight = maxBillHeight;
              console.warn(`Bill ${processedBills + 1} is very tall, scaling down by ${(scaleFactor * 100).toFixed(1)}%`);
            }
            
            // Check if we need a new page - ensure the entire bill fits
            if (currentY + actualHeight > pageHeight - margin) {
              pdf.addPage();
              currentY = margin; // Reset to top of new page
            }
            
            // Add the bill to the current position
            pdf.addImage(imgData, 'JPEG', margin, currentY, pdfWidth, actualHeight);
            
            // Update current Y position for next bill (add some spacing)
            currentY += actualHeight + 10; // 10mm spacing between bills
            
            processedBills++;
            
            // Update progress
            const progress = Math.round((processedBills / totalBills) * 100);
            setExportProgress(progress);
            console.log(`Processed ${processedBills}/${totalBills} bills (${progress}%) - Height: ${actualHeight.toFixed(1)}mm, Y: ${currentY.toFixed(1)}mm`);
            
          } catch (error) {
            console.error('Error processing bill:', error);
          } finally {
            // Clean up
            if (document.body.contains(tempDiv)) {
              document.body.removeChild(tempDiv);
            }
          }
          
          // Small delay to prevent browser freezing
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // Force garbage collection between batches
        if (window.gc) {
          window.gc();
        }
      }
      
      pdf.save(`expBills_${period}_${year}.pdf`);
      
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Error generating PDF. Please try again with a smaller dataset or contact support.');
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleExportSummaryPDF = async () => {
    if (!filteredBills || filteredBills.length === 0) return;
    setExporting(true);
    setExportType('summary');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add header
      const logoUrl = rootUrl(`/${allData?.council?.[0]?.path}`);
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(allData?.council?.[0]?.name || 'Council', pageWidth / 2, 20, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Property Rates Bills Summary - ${period === 'jan-jun' ? 'Jan - Jun' : 'Jul - Dec'} ${year}`, pageWidth / 2, 30, { align: 'center' });
      
      // Calculate totals
      const totalBills = filteredBills.length;
      const totalAmount = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0);
      const totalOpeningBalance = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.opening_balance || 0), 0);
      const totalActualBalance = totalOpeningBalance + totalAmount;
      
      // Add summary information
      let yPos = 50;
      pdf.setFontSize(10);
      pdf.text(`Total Bills: ${totalBills}`, 20, yPos);
      yPos += 10;
      pdf.text(`Total Bill Amount: K${totalAmount.toLocaleString()}`, 20, yPos);
      yPos += 10;
      pdf.text(`Total Opening Balance: K${totalOpeningBalance.toLocaleString()}`, 20, yPos);
      yPos += 10;
      pdf.text(`Total Actual Balance: K${totalActualBalance.toLocaleString()}`, 20, yPos);
      yPos += 20;
      
      // Add table headers
      const tableHeaders = ['Plot', 'Location', 'Customer', 'Bill Amount', 'Opening Balance', 'Actual Balance'];
      const colWidths = [25, 30, 40, 25, 25, 25];
      let xPos = 20;
      
      pdf.setFont(undefined, 'bold');
      tableHeaders.forEach((header, index) => {
        pdf.text(header, xPos, yPos);
        xPos += colWidths[index];
      });
      yPos += 10;
      
      // Add table rows
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8);
      
      filteredBills.forEach((bill, index) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }
        
        const customerName = allData?.suppliers.find(a => a.id == bill.customer)?.name || '-';
        const actualBalance = parseFloat(bill.opening_balance || 0) + parseFloat(bill.amount || 0);
        
        const rowData = [
          bill.property_no || bill.Property_no || '-',
          bill.location || '-',
          customerName.length > 20 ? customerName.substring(0, 20) + '...' : customerName,
          `K${parseFloat(bill.amount || 0).toLocaleString()}`,
          `K${parseFloat(bill.opening_balance || 0).toLocaleString()}`,
          `K${actualBalance.toLocaleString()}`
        ];
        
        xPos = 20;
        rowData.forEach((data, colIndex) => {
          pdf.text(data, xPos, yPos);
          xPos += colWidths[colIndex];
        });
        yPos += 8;
      });
      
      pdf.save(`bills_summary_${period}_${year}.pdf`);
      
    } catch (error) {
      console.error('Summary PDF export error:', error);
      alert('Error generating summary PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full bg-white min-h-[400px] rounded-2xl mt-6 p-4 md:p-6 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-bold text-xl md:text-2xl text-gray-800">
            Actual Bills
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate and export property rates bills by period, year, and location.
          </p>
        </div>
      </div>

      {/* Filters and actions */}
      <form
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block mb-1 text-sm font-semibold text-gray-700">
              Period
            </label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={period}
              onChange={e => setPeriod(e.target.value)}
            >
              <option value="jan-jun">Jan - Jun</option>
              <option value="jul-dec">Jul - Dec</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 text-sm font-semibold text-gray-700">
              Year
            </label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={year}
              onChange={e => setYear(e.target.value)}
            >
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          {bills && locations.length > 0 && (
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-700">
                Location
              </label>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={locationFilter}
                onChange={e => setLocationFilter(e.target.value)}
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className="inline-flex items-center justify-center bg-blue-950 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-900 disabled:opacity-60"
            disabled={loading}
          >
            {loading && (
              <FaSpinner className="animate-spin inline mr-2" />
            )}
            Run Query
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center bg-green-700 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-green-800 disabled:opacity-60"
            onClick={handleExportPDF}
            disabled={exporting}
          >
            {exporting && exportType === 'individual' && (
              <FaSpinner className="animate-spin inline mr-2" />
            )}
            Export Individual Bills
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center bg-blue-700 text-white px-4 py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
            onClick={handleExportSummaryPDF}
            disabled={exporting}
          >
            {exporting && exportType === 'summary' && (
              <FaSpinner className="animate-spin inline mr-2" />
            )}
            Export Summary
          </button>
        </div>
      </form>

      {/* Export Progress Indicator */}
      {exporting && (
        <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-800">
              {exportType === 'individual'
                ? 'Exporting individual bills...'
                : 'Generating summary report...'}
            </span>
            <span className="text-xs font-semibold text-blue-700">
              {exportProgress}%
            </span>
          </div>
          <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${exportProgress}%` }}
            ></div>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            {exportType === 'individual'
              ? 'Processing bills in batches to handle large datasets efficiently...'
              : 'Preparing a high-level summary of all bills in the selected period.'}
          </p>
        </div>
      )}

      {/* Large Dataset Warning */}
      {filteredBills && filteredBills.length > 100 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-yellow-900">
                Large Dataset Detected ({filteredBills.length} bills)
              </h3>
              <div className="mt-1 text-xs md:text-sm text-yellow-800 space-y-1">
                <p>For better performance with large datasets, consider:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Using the "Export Summary" option for faster processing</li>
                  <li>Filtering by location to reduce the dataset size</li>
                  <li>
                    Individual bill export may take longer but provides detailed
                    invoices
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-10">
          <FaSpinner className="animate-spin text-blue-950" size={40} />
        </div>
      )}
      {error && (
        <div className="mb-4 text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {filteredBills && Array.isArray(filteredBills) && filteredBills.length > 0 && (
        <div
          ref={componentRef}
          className="mt-2 rounded-xl border border-gray-100 bg-white overflow-hidden"
        >
          <DataTable
            columns={columns}
            data={filteredBills}
            pagination
            highlightOnHover
            striped
            responsive
          />
        </div>
      )}
      {filteredBills &&
        Array.isArray(filteredBills) &&
        filteredBills.length === 0 &&
        !loading && (
          <div className="mt-4 text-sm font-medium text-gray-600">
            No bills found for the selected period.
          </div>
        )}
    </div>
  );
};

export default ActualBills; 